/* eslint-disable react-hooks/exhaustive-deps */
import t from '@rcm/i18n-client';
import React, { useState, useEffect } from 'react';
import { Form, InputTextField, SubmitButton } from 'forms';
import './GASettingsForm.css';
import type { AxiosError } from 'axios';
import axios from 'axios';
import type { pwsData } from '../types/pwsData';
import type { GASettingsRequestData } from '../types/GASettingsRequestData.server';
import getAppState from 'app-state';
import LoadingSpinner from 'loading-spinner/LoadingSpinner';

interface Props {
  data: pwsData;
  isEmployee: boolean;
  userId: string;
}

interface site {
  countryIso3: string;
  siteId: number;
}

const GASettingsForm = (props: Props) => {
  const [errorMsg, setErrorMsg] = useState<string>(null);
  const [success, setSuccess] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [trackingId, setTrackingId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [accountInfo, setAccountInfo] = useState<GASettingsRequestData>(null);
  const { data, isEmployee, userId } = props;
  const handleChangeTrackingId = (value: string) => {
    setTrackingId(value);
  };
  const isEditing = localStorage.getItem('isEditing') ?? false;
  const site: site = getAppState('site');

  // success banner
  const successfullyUpdated = (shouldShow: boolean) => {
    if (!shouldShow) return null;

    return (
      <div className="alert alert-success">
        <div className="message">{t('Your changes have been saved.')}</div>
      </div>
    );
  };

  // error banner
  const errorNotice = (message: string) => {
    if (!message) return null;

    return (
      <div className="alert alert-warning" role="alert">
        <div className="message">{t(message)}</div>
      </div>
    );
  };

  // extra styles to get the list to display correctly
  const gaList = {
    listStyleType: 'none',
    counterReset: 'elementcounter',
    paddingLeft: 0,
  };

  const getSiteData = (siteId) => {
    axios
      .get('/api/v2/rcm-ga/' + siteId)
      .then(function (response) {
        try {
          setTrackingId(response.data[0].trackingId);
          setAccountInfo(response.data[0]);
        } catch (e) {
          setIsNew(true);
        }
      })
      .catch(function (error: AxiosError) {
        if (error.response.status === 404) {
          setIsNew(true);
        } else {
          setErrorMsg(error.message);
        }
      });
  };

  // retrieves initial GA data on page load, does not run when adding the component via CMS
  useEffect(() => {
    // only call API when the component isn't being generated during page building and either:
    // (1) it's a PWS site and the logged in user is the owner or
    // (2) an employee is logged in
    if (!isEditing && (data?.isOwner || isEmployee)) {
      getSiteData(site.siteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // disable form/button if the component is being created or if the user is neither a PWS owner nor an employee
    setDisabled(submitting || (!data?.isOwner && !isEmployee));
  }, [submitting, data, isEmployee]);

  // updates trackingId in the account object
  useEffect(() => {
    setAccountInfo((currState) => ({
      ...currState,
      trackingId: trackingId.trim(),
      userId: userId,
      isEmployee: isEmployee,
    }));
  }, [trackingId]);

  // updates userId in the account object
  useEffect(() => {
    setAccountInfo((currState) => ({
      ...currState,
      userId: userId,
    }));
  }, [userId]);

  const submitData = () => {
    setSubmitting(true);
    setSuccess(false);

    if (!trackingId || trackingId.trim() === '') {
      setErrorMsg('Tracking ID field cannot be empty.');
      setSubmitting(false);
      return;
    }
    if (isNew) {
      axios
        .post('/api/v2/rcm-ga/' + site.siteId, accountInfo)
        .then(function (response) {
          if (response.status == 200) {
            setSuccess(true);
            setErrorMsg(null);
            setIsNew(false);
            getSiteData(site.siteId);
          }
        })
        .catch(function (error: AxiosError) {
          setErrorMsg(error.message);
        });
    } else {
      axios
        .patch('/api/v2/rcm-ga/' + site.siteId, accountInfo)
        .then(function (response) {
          if (response.status == 200 || response.status == 204) {
            setSuccess(true);
            setErrorMsg(null);
          }
        })
        .catch(function (error: AxiosError) {
          setErrorMsg(error.message);
        });
    }
    setSubmitting(false);
  };

  return (
    <div>
      <h1>{t('Google Analytics Settings')}</h1>
      <Form onSubmit={submitData} className="steps">
        {successfullyUpdated(success && !errorMsg)}
        {errorNotice(errorMsg)}
        <p>
          {t('This page allows you to connect your website to Google Analytics.')}
          <br />
          <span>
            {t('The Google Analytics dashboard is provided by Google at ')}
            <a href="https://analytics.google.com">https://analytics.google.com.</a>
          </span>
          <br />
          {t('Google Analytics Setup Instructions:')}
          <br />
          <ol style={gaList} className="ga-steps">
            <li>{t('Login to Google Analytics and get a Tracking ID for your website from Google.')}</li>
            <li>{t('Enter your tracking ID in the field below and click "save".')}</li>
            <li>
              <span>
                {t('View analytics from your website at ')}
                <a href="https://analytics.google.com">https://analytics.google.com.</a>
              </span>
            </li>
          </ol>
        </p>
        <InputTextField
          type="text"
          label={t('Google Analytics Tracking ID (should look similar to UA-000000-01):')}
          id="trackingId"
          onValueChange={handleChangeTrackingId}
          disabled={disabled}
          value={trackingId}
        />
        <SubmitButton disabled={disabled} className="btn btn-primary">
          <span>{t('Save')}</span>
        </SubmitButton>
        {submitting ? (
          <div className="submitting-form">
            <LoadingSpinner />
          </div>
        ) : null}
      </Form>
    </div>
  );
};

export default GASettingsForm;
